/**
 * «Surtido» — the reads, one hook each. A page reads its plan's totals and its first page, never the
 * plan (ADR 9076): more rows come by `next_cursor` when the planner asks, and only a FILE walks to the end.
 *
 * The contract's pages state no total of their own: the totals a page may state are the plan's
 * (`rowCount`, `lineCount`, `linesByAction`, `nodeCount`, `storeCount`), which is why a count over a
 * filtered list says «at least» when more pages exist (`./frame.tsx`, `CountLine`).
 */

import { useMemo } from 'react';

import { useApiQuery, useReasonLabels, type ReasonLabels } from '@/api';
import {
  assortmentListPlans, assortmentReadLineWhy, assortmentReadPlan, assortmentReadReasonLabels, listLines, listPlacements, listStoreRows,
  readBands, readBases, readMatrix, readPlanAttributes, readReasonCodes, readStore, readStoreMix,
  type LineView, type MatrixCellView, type PlacementView, type StoreNodeView,
} from '@/api/generated';
import { useCursorPages, type CursorPages } from '@/planning';

import { NO_ATTRIBUTES, type AttributeFilter } from './scope';

/** Rows per page of the lists a page draws. */
export const PAGE_SIZE = 100;
/** Cells per page of the matrix: a plan at category level holds a few hundred. */
export const MATRIX_PAGE_SIZE = 500;

/** The plans the person may read, newest first, the first page. */
export function usePlans() {
  return useApiQuery(assortmentListPlans, { query: { limit: 50 } });
}

export function usePlan(planId: string | null) {
  return useApiQuery(assortmentReadPlan, { path: { plan_id: planId ?? '' } }, { enabled: planId !== null });
}

export function useBases(countryKey: string | null) {
  return useApiQuery(readBases, { query: { country_key: countryKey ?? '' } }, { enabled: countryKey !== null && countryKey !== '' });
}

export function useReasonCodes() {
  return useApiQuery(readReasonCodes, {});
}

/** The platform's words for every reason code the plan writes (`./reasons.ts`). */
export function useReasonVocabulary(): ReasonLabels {
  return useReasonLabels(assortmentReadReasonLabels);
}

export function useBands(planId: string | null) {
  return useApiQuery(readBands, { query: { plan_id: planId ?? '' } }, { enabled: planId !== null });
}

/** The query a gender and a brand make, for the reads that take them. */
export function attributeQuery(attrs: AttributeFilter): { readonly gender?: string; readonly brand?: string } {
  return { ...(attrs.gender === null ? {} : { gender: attrs.gender }), ...(attrs.brand === null ? {} : { brand: attrs.brand }) };
}

/** The store's placements and totals; narrowed to a gender and a brand the totals are those of the plan run on those articles. */
export function useStorePage(planId: string | null, storeKey: string | null, attrs: AttributeFilter = NO_ATTRIBUTES) {
  return useApiQuery(
    readStore,
    { path: { plan_id: planId ?? '', store_key: storeKey ?? '' }, query: attributeQuery(attrs) },
    { enabled: planId !== null && storeKey !== null },
  );
}

/** The genders and brands a plan's filters offer, and whether it can be filtered at all (a plan written before the figures were kept cannot). */
export function useAttributes(planId: string | null) {
  return useApiQuery(readPlanAttributes, { path: { plan_id: planId ?? '' } }, { enabled: planId !== null });
}

/** How a store's options and pairs divide by gender and brand, over all its categories or one. */
export function useStoreMix(planId: string | null, storeKey: string | null, nodeId: string | null) {
  return useApiQuery(
    readStoreMix,
    { path: { plan_id: planId ?? '', store_key: storeKey ?? '' }, query: nodeId === null ? {} : { node_id: nodeId } },
    { enabled: planId !== null && storeKey !== null },
  );
}

export function useLineWhy(planId: string | null, lineId: string | null) {
  return useApiQuery(
    assortmentReadLineWhy, { path: { plan_id: planId ?? '', line_id: lineId ?? '' } }, { enabled: planId !== null && lineId !== null },
  );
}

/** The matrix cells, from the first page; `node` narrows to one category, `cluster` to one cluster, `attrs` to a gender and a brand. */
export function useMatrix(
  planId: string | null, node: string | null = null, cluster: string | null = null, attrs: AttributeFilter = NO_ATTRIBUTES,
): CursorPages<typeof readMatrix, MatrixCellView> {
  const signature = `matrix|${planId ?? ''}|${node ?? ''}|${cluster ?? ''}|${attrs.gender ?? ''}|${attrs.brand ?? ''}`;
  return useCursorPages<typeof readMatrix, MatrixCellView>(
    readMatrix,
    signature,
    (cursor) => ({
      path: { plan_id: planId ?? '' },
      query: {
        limit: MATRIX_PAGE_SIZE, ...(node === null ? {} : { node_id: node }), ...(cluster === null ? {} : { cluster_id: cluster }), ...attributeQuery(attrs),
        ...(cursor === null ? {} : { cursor }),
      },
    }),
    { enabled: planId !== null },
  );
}

/** What narrows the store × category rows — the same filters the file takes. */
export interface RowFilter {
  readonly storeKey?: string | null;
  readonly nodeId?: string | null;
  readonly clusterId?: string | null;
  readonly bandIndex?: number | null;
  readonly flag?: 'under_assorted' | 'over_assorted' | 'missing' | 'bounded' | 'refused' | null;
  readonly decision?: 'accept' | 'reject' | 'undecided' | null;
  readonly actionNeeded?: boolean;
  readonly gender?: string | null;
  readonly brand?: string | null;
}

/** The query of one page of rows. */
export function rowQuery(filter: RowFilter, cursor: string | null, limit: number | null = PAGE_SIZE) {
  return {
    ...(limit === null ? {} : { limit }),
    ...(filter.storeKey ? { store_key: filter.storeKey } : {}),
    ...(filter.nodeId ? { node_id: filter.nodeId } : {}),
    ...(filter.clusterId ? { cluster_id: filter.clusterId } : {}),
    ...(filter.bandIndex === null || filter.bandIndex === undefined ? {} : { band_index: filter.bandIndex }),
    ...(filter.flag ? { flag: filter.flag } : {}),
    ...(filter.decision ? { decision: filter.decision } : {}),
    ...(filter.actionNeeded === true ? { action_needed: true } : {}),
    ...(filter.gender ? { gender: filter.gender } : {}),
    ...(filter.brand ? { brand: filter.brand } : {}),
    ...(cursor === null ? {} : { cursor }),
  };
}

export function useStoreRows(planId: string | null, filter: RowFilter, enabled = true): CursorPages<typeof listStoreRows, StoreNodeView> {
  const signature = `rows|${planId ?? ''}|${JSON.stringify(filter)}`;
  return useCursorPages<typeof listStoreRows, StoreNodeView>(
    listStoreRows,
    signature,
    (cursor) => ({ path: { plan_id: planId ?? '' }, query: rowQuery(filter, cursor) }),
    { enabled: planId !== null && enabled },
  );
}

/** What narrows the store × article lines. */
export interface LineFilter {
  readonly storeKey?: string | null;
  readonly nodeId?: string | null;
  readonly action?: 'add_now' | 'add_next_buy' | 'remove' | 'keep' | null;
  readonly gender?: string | null;
  readonly brand?: string | null;
}

export function lineQuery(filter: LineFilter, cursor: string | null, limit: number | null = PAGE_SIZE) {
  return {
    ...(limit === null ? {} : { limit }),
    ...(filter.storeKey ? { store_key: filter.storeKey } : {}),
    ...(filter.nodeId ? { node_id: filter.nodeId } : {}),
    ...(filter.action ? { action: filter.action } : {}),
    ...(filter.gender ? { gender: filter.gender } : {}),
    ...(filter.brand ? { brand: filter.brand } : {}),
    ...(cursor === null ? {} : { cursor }),
  };
}

export function useLines(planId: string | null, filter: LineFilter, enabled = true): CursorPages<typeof listLines, LineView> {
  const signature = `lines|${planId ?? ''}|${JSON.stringify(filter)}`;
  return useCursorPages<typeof listLines, LineView>(
    listLines,
    signature,
    (cursor) => ({ path: { plan_id: planId ?? '' }, query: lineQuery(filter, cursor) }),
    { enabled: planId !== null && enabled },
  );
}

export function usePlacements(planId: string | null, bandIndex: number | null, storeKey: string | null = null): CursorPages<typeof listPlacements, PlacementView> {
  const signature = `placements|${planId ?? ''}|${bandIndex ?? ''}|${storeKey ?? ''}`;
  return useCursorPages<typeof listPlacements, PlacementView>(
    listPlacements,
    signature,
    (cursor) => ({
      path: { plan_id: planId ?? '' },
      query: {
        limit: PAGE_SIZE,
        ...(bandIndex === null ? {} : { band_index: bandIndex }),
        ...(storeKey === null ? {} : { store_key: storeKey }),
        ...(cursor === null ? {} : { cursor }),
      },
    }),
    { enabled: planId !== null },
  );
}

/** The distinct stores of rows read — the pages' own stores, for a chooser; never a count of the plan. */
export function useDistinct(values: readonly string[]): readonly string[] {
  return useMemo(() => [...new Set(values)].sort(), [values]);
}

/** A band's range by its index, from the plan's bands: what names a store's group on a page that holds only its index. */
export function useBandRanges(planId: string | null): (index: number | null | undefined) => { readonly low: string | null; readonly high: string | null; readonly measure: string | null } {
  const bands = useBands(planId);
  return useMemo(() => {
    const list = bands.data?.bands ?? [];
    return (index) => {
      const band = index === null || index === undefined ? undefined : list.find((one) => one.bandIndex === index);
      return { low: band?.low ?? null, high: band?.high ?? null, measure: band?.measure ?? null };
    };
  }, [bands.data]);
}
